import { ButtonHTMLAttributes, forwardRef } from 'react';
import Spinner from './Spinner';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			size = 'md',
			isLoading,
			className = '',
			children,
			disabled,
			style,
			...props
		},
		ref
	) => {
		const baseClasses =
			'font-semibold rounded-lg cursor-pointer transition-all duration-300 inline-flex items-center justify-center gap-2';

		const variantClasses = {
			primary:
				'text-white hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none',
			secondary:
				'bg-white text-gray-600 border-2 border-gray-200 hover:border-blue-600 hover:text-blue-600 disabled:bg-gray-100 disabled:hover:border-gray-200 disabled:hover:text-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:border-blue-400 dark:hover:text-blue-400 dark:disabled:bg-gray-800',
			danger: 'bg-white text-red-600 border-2 border-red-200 hover:bg-red-50 hover:border-red-400 disabled:bg-gray-100 dark:bg-gray-700 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20 dark:disabled:bg-gray-800',
			success:
				'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 dark:bg-green-500 dark:hover:bg-green-600 dark:disabled:bg-gray-600',
		};

		const sizeClasses = {
			sm: 'px-3 py-1.5 text-sm',
			md: 'px-6 py-3 text-sm',
			lg: 'px-6 py-3.5 text-base',
		};

		// Primary button uses gradient background
		const primaryGradientStyle =
			variant === 'primary'
				? {
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						...style,
					}
				: style;

		return (
			<button
				ref={ref}
				className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
				disabled={disabled || isLoading}
				style={primaryGradientStyle}
				{...props}
			>
				{isLoading && <Spinner size="sm" />}
				{children}
			</button>
		);
	}
);

Button.displayName = 'Button';

export default Button;
